import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { motion, AnimatePresence } from "framer-motion";
import {
    Download, Search, X,
    Lightbulb, Volume2, Monitor, Settings2, Zap, Star,
    Music, Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Package, Tag, Mic2
} from "lucide-react";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import { api, BASE } from "../services/api";

const ICON_MAP = {
    Lightbulb, Volume2, Monitor, Settings2, Zap, Star,
    Music, Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Package, Tag, Mic2
};

function hexToRgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
}

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Animated counter ─────────────────────────────────────────────────────────
function Counter({ target, suffix = "" }) {
    const [n, setN] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (!e.isIntersecting) return;
            obs.disconnect();
            const end = parseInt(target) || 0;
            if (!end) { setN(target); return; }
            const dur = 1800;
            const start = performance.now();
            const tick = (now) => {
                const p = Math.min((now - start) / dur, 1);
                setN(Math.floor(end * (1 - Math.pow(1 - p, 3))));
                if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }, { rootMargin: "-40px" });
        obs.observe(el);
        return () => obs.disconnect();
    }, [target]);

    return <span ref={ref}>{n}{suffix}</span>;
}

// ── Category picker ───────────────────────────────────────────────────────────
function CategoryPicker({ value, onChange, counts, cats }) {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return (
        <div className="flex flex-wrap gap-2.5">
            <button
                onClick={() => onChange("all")}
                className={`flex items-center gap-2 h-10 px-5 rounded-xl border text-sm font-mono tracking-wide transition-all duration-200 ${
                    value === "all"
                        ? "border-white/30 bg-white/8 text-white shadow-[0_0_16px_rgba(255,255,255,0.06)]"
                        : "border-white/8 text-zinc-500 hover:border-white/20 hover:text-zinc-300"
                }`}
            >
                Všetko
                <span className={`text-xs tabular-nums ${value === "all" ? "text-zinc-400" : "text-zinc-700"}`}>{total}</span>
            </button>

            {Object.entries(cats).map(([key, cat]) => {
                const Icon = cat.icon;
                const active = value === key;
                return (
                    <motion.button
                        key={key}
                        onClick={() => onChange(key)}
                        whileTap={{ scale: 0.96 }}
                        style={active ? {
                            borderColor: `rgba(${cat.rgb},0.55)`,
                            background: `rgba(${cat.rgb},0.09)`,
                            color: cat.color,
                            boxShadow: `0 0 22px rgba(${cat.rgb},0.14)`,
                        } : {}}
                        className={`flex items-center gap-2 h-10 px-5 rounded-xl border text-sm font-mono tracking-wide transition-all duration-200 ${
                            active ? "" : "border-white/8 text-zinc-500 hover:border-white/20 hover:text-zinc-300"
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5 shrink-0" />
                        {cat.label}
                        <span className={`text-xs tabular-nums ${active ? "opacity-60" : "text-zinc-700"}`}>
                            {counts[key] ?? 0}
                        </span>
                    </motion.button>
                );
            })}
        </div>
    );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
    return (
        <div className="rounded-2xl overflow-hidden border border-white/6 bg-nova-surface/30 animate-pulse">
            <div className="h-44 bg-white/4" />
            <div className="p-5 space-y-3">
                <div className="h-4 w-2/3 bg-white/6 rounded" />
                <div className="h-3 w-1/2 bg-white/4 rounded" />
                <div className="grid grid-cols-2 gap-1.5 pt-3">
                    {[0,1,2,3].map(i => <div key={i} className="h-10 bg-white/4 rounded-lg" />)}
                </div>
            </div>
        </div>
    );
}

// ── Equipment card ────────────────────────────────────────────────────────────
function EquipCard({ item, index, cats }) {
    const cat   = cats[item.category] || {};
    const Icon  = cat.icon  || Zap;
    const color = cat.color || "#00BFFF";
    const rgb   = cat.rgb   || "0,191,255";
    const img   = resolveImg(item.image_url);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.32) }}
            className="group relative flex flex-col rounded-2xl overflow-hidden border border-white/8 bg-nova-surface/40 cursor-default
                       hover:border-[rgba(var(--cr),0.45)] hover:-translate-y-1.5
                       transition-all duration-300"
            style={{
                "--cr": rgb,
                ["--hover-shadow"]: `0 12px 40px rgba(${rgb},0.18)`,
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 12px 40px rgba(${rgb},0.18)`}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
        >
            {/* Left accent bar */}
            <div className="absolute left-0 top-0 bottom-0 w-[3px] z-10" style={{ background: `linear-gradient(to bottom, ${color}, rgba(${rgb},0.2))` }} />

            {/* Image / icon area */}
            <div className="relative h-44 overflow-hidden bg-nova-black/60 shrink-0">
                {img ? (
                    <img
                        src={img}
                        alt={`${item.brand} ${item.model}`}
                        className="w-full h-full object-cover opacity-75 group-hover:opacity-95 group-hover:scale-105 transition-all duration-500"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-16 h-16 opacity-[0.07]" style={{ color }} />
                        <div className="absolute inset-0" style={{
                            background: `radial-gradient(ellipse 70% 70% at 50% 50%, rgba(${rgb},0.07), transparent)`
                        }} />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-nova-black/95 via-nova-black/25 to-transparent" />

                {/* Qty badge */}
                {item.qty && (
                    <div
                        className="absolute top-3 right-3 font-mono text-xs px-2.5 py-1 rounded-full border backdrop-blur-sm"
                        style={{ color, borderColor: `rgba(${rgb},0.45)`, background: `rgba(${rgb},0.12)` }}
                    >
                        {item.qty}
                    </div>
                )}

                {/* Featured star */}
                {item.featured && (
                    <div className="absolute top-3 left-4 flex items-center gap-1 bg-amber-400/15 border border-amber-400/30 rounded-full px-2 py-0.5">
                        <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                        <span className="font-mono text-[9px] text-amber-400 uppercase tracking-wider">Top</span>
                    </div>
                )}

                {/* Category chip */}
                <div className="absolute bottom-3 left-4">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: `rgba(${rgb},0.75)` }}>
                        {cat.label}
                    </span>
                </div>
            </div>

            {/* Text content */}
            <div className="flex flex-col flex-1 p-5 pl-6">
                <div className="mb-3">
                    <div className="font-heading font-black text-lg text-white tracking-tight leading-none">{item.brand}</div>
                    <div className="font-heading text-sm font-semibold text-zinc-400 mt-0.5 leading-snug">{item.model}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mt-1.5 leading-none">{item.type}</div>
                </div>

                {item.specs?.length > 0 && (
                    <div className="grid grid-cols-2 gap-1.5 pt-3 border-t border-white/5 mt-auto">
                        {item.specs.slice(0, 4).map((s, i) => (
                            <div key={i} className="rounded-lg bg-white/3 border border-white/5 px-2.5 py-2 hover:bg-white/5 transition-colors">
                                <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5 leading-none">{s.k}</div>
                                <div className="text-white text-xs font-medium leading-snug">{s.v}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TechnikaPage() {
    const [filter, setFilter]       = useState("all");
    const [search, setSearch]       = useState("");
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [cats, setCats]           = useState({});

    useEffect(() => {
        api.getTechnika().then(setEquipment).catch(() => {}).finally(() => setLoading(false));
        api.getTechnikaCategories().then(list => {
            const map = {};
            list.forEach(c => {
                map[c.slug] = { ...c, icon: ICON_MAP[c.icon_name] || Zap, rgb: hexToRgb(c.color) };
            });
            setCats(map);
        }).catch(() => {});
    }, []);

    const counts = Object.keys(cats).reduce((acc, k) => {
        acc[k] = equipment.filter(i => i.category === k).length;
        return acc;
    }, {});

    const filtered = equipment
        .filter(i => filter === "all" || i.category === filter)
        .filter(i => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (
                i.brand.toLowerCase().includes(q) ||
                i.model.toLowerCase().includes(q) ||
                (i.type  || "").toLowerCase().includes(q) ||
                i.specs?.some(s => s.v.toLowerCase().includes(q))
            );
        });

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title="Technika & Inventár"
                description="Kompletný inventár profesionálnej AV techniky — L-Acoustics K2 line array, Robe BMFL moving heady, LED wally a riadiacie systémy. Stiahnite tech rider PDF."
                keywords="profesionálna technika, prenájom zvuku, prenájom svetiel, L-Acoustics, Robe, LED wall, ozvučenie, osvetlenie, tech rider, Slovensko"
                canonical="/technika"
            />

            <Navigation />

            {/* ── Hero ── */}
            <div className="relative overflow-hidden bg-nova-black pt-20">
                {/* Background glow */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-nova-cyan/6 rounded-full blur-[120px]" />
                    <div className="absolute top-10 right-1/4 w-[400px] h-[300px] bg-purple-500/5 rounded-full blur-[100px]" />
                    <div className="absolute inset-0 grid-bg opacity-20" />
                </div>

                {/* Decorative large text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
                    <span className="font-heading font-black text-[12vw] text-white/[0.025] tracking-tighter whitespace-nowrap">
                        TECHNIKA
                    </span>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-20 pb-16">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-nova-cyan mb-5 flex items-center gap-2">
                            <span className="inline-block w-6 h-px bg-nova-cyan/60" />
                            Inventár techniky
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-[4.5rem] font-black tracking-tighter leading-[1.02] mb-6 max-w-3xl">
                            Profesionálna technika{" "}
                            <span className="text-nova-cyan">na každý event</span>
                        </h1>
                        <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-14">
                            Špičkové svetelné, zvukové a LED systémy od overených značiek — pravidelne servisované, pripravené na nasadenie.
                        </p>
                    </motion.div>

                </div>
            </div>

            {/* ── Content ── */}
            <main className="max-w-7xl mx-auto px-6 sm:px-12 pb-24">

                {/* Filters row */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 py-6 border-y border-white/6"
                >
                    <CategoryPicker value={filter} onChange={v => { setFilter(v); setSearch(""); }} counts={counts} cats={cats} />

                    <div className="relative shrink-0 w-full sm:w-60">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Hľadať zariadenie…"
                            className="w-full h-10 pl-9 pr-8 rounded-xl bg-nova-surface/40 border border-white/8 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-nova-cyan/45 transition-colors"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white transition-colors">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </motion.div>

                {/* Result info */}
                {!loading && (search || filter !== "all") && (
                    <div className="mb-6 font-mono text-xs text-zinc-600 uppercase tracking-wider">
                        {filtered.length === 0 ? "Žiadne výsledky" : `${filtered.length} zariadení`}
                        {search && <span className="text-zinc-500"> pre „{search}"</span>}
                    </div>
                )}

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-28 gap-4">
                        <Zap className="w-10 h-10 text-zinc-700" />
                        <p className="text-zinc-600 text-sm font-mono uppercase tracking-wider">Žiadne zariadenia nenájdené</p>
                    </div>
                ) : (
                    <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((item, i) => (
                                <EquipCard key={item.id} item={item} index={i} cats={cats} />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* ── Tech rider CTA ── */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6 }}
                    className="relative mt-20 rounded-2xl overflow-hidden border border-white/8 bg-nova-surface/30 p-8 sm:p-12"
                >
                    {/* Background glow */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_100%_50%,rgba(0,191,255,0.06),transparent)] pointer-events-none" />
                    <div className="absolute bottom-0 right-0 w-64 h-64 bg-nova-cyan/5 rounded-full blur-[80px] pointer-events-none" />

                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-nova-cyan mb-3 flex items-center gap-2">
                                <span className="w-4 h-px bg-nova-cyan/60 inline-block" />
                                Tech rider
                            </div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                                Potrebujete kompletný tech rider?
                            </h2>
                            <p className="text-zinc-400 text-sm max-w-lg leading-relaxed">
                                Stiahnite si náš technický rider vo formáte PDF — celý inventár, technické parametre a požiadavky na prípravu miesta.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                            <a
                                href="/tech-rider-kb-music.pdf"
                                className="inline-flex items-center gap-2 h-11 px-6 bg-nova-cyan text-black font-semibold text-sm rounded-xl hover:saturate-125 transition-all hover:shadow-[0_0_24px_rgba(0,153,255,0.4)]"
                            >
                                <Download className="w-4 h-4" /> Stiahnuť PDF
                            </a>
                            <Link
                                to="/"
                                className="inline-flex items-center justify-center h-11 px-6 border border-white/12 text-white text-sm font-medium rounded-xl hover:border-white/28 hover:bg-white/5 transition-all"
                            >
                                Späť na web
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </main>

            <Footer />
        </div>
    );
}
